import React, { useState, useEffect, useRef } from 'react'
import VisitsService from '../../../services/VisitsService'
import Pagination from '../../../components/UI/TableGrid/Pagination'
import SortableTh from './SortableTh'
import { isWorkCompVisitCategory } from '../../../utils/visitUtils'

const IncompleteCharting = ({ dateFilter = 'year', filters = {}, Loader, Toast, navigate }) => {
  const [tableData, setTableData] = useState([])
  const [allData, setAllData] = useState([]) // Store all data for pagination
  const [loading, setLoading] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [initialPosition, setInitialPosition] = useState(null)
  const containerRef = useRef(null)
  const originalPositionRef = useRef(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortColumn, setSortColumn] = useState('visit_date')
  const [sortDirection, setSortDirection] = useState('desc')
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRecords: 0
  })

  useEffect(() => {
    fetchIncompleteCharting()
  }, [dateFilter, filters, isFullscreen, sortColumn, sortDirection])

  useEffect(() => {
    if (isFullscreen && allData.length > 0) {
      applyPagination()
    }
  }, [pagination.page, pagination.perPage, allData, isFullscreen])

  const fetchIncompleteCharting = async (searchValue = null) => {
    setLoading(true)
    Loader(true)
    try {
      const currentSearchTerm = searchValue !== null ? searchValue : searchTerm
      const payload = {
        date_filter: dateFilter || 'year',
        sort_by: sortColumn,
        sort_order: sortDirection,
        ...filters
      }
      if (isFullscreen && currentSearchTerm && currentSearchTerm.trim()) {
        payload.patient_name = currentSearchTerm.trim()
      }

      if (!isFullscreen) {
        payload.limit = 5
      }
      const response = await VisitsService.getIncompleteCharting(payload)
      if (response && response.incomplete_charting && response.incomplete_charting.length > 0) {
        const data = response.incomplete_charting
        if (isFullscreen) {
          setAllData(data)
          setPagination(prev => ({ ...prev, totalRecords: data.length, page: 1 }))
          setTableData(data.slice(0, pagination.perPage))
        } else {
          setTableData(data)
        }
      } else {
        setTableData([])
        setAllData([])
        setPagination(prev => ({ ...prev, totalRecords: 0, page: 1 }))
      }
    } catch (error) {
      console.error('Error fetching incomplete charting:', error)
      setTableData([])
      setAllData([])
    } finally {
      setLoading(false)
      Loader(false)
    }
  }

  const applyPagination = () => {
    const start = (pagination.page - 1) * pagination.perPage
    const end = start + pagination.perPage
    const paginatedData = allData.slice(start, end)
    setTableData(paginatedData)
  }

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination)
  }

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(col)
      setSortDirection('asc')
    }
    setPagination(prev => ({ ...prev, page: 1 }))
  }
  const SettingsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.4155 6.58674C10.1962 7.36741 10.1962 8.63474 9.4155 9.41674C8.63483 10.1974 7.3675 10.1974 6.5855 9.41674C5.80483 8.63608 5.80483 7.36874 6.5855 6.58674C7.3675 5.80474 8.63417 5.80474 9.4155 6.58674"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 7.99992C2 7.56059 2.10133 7.12592 2.29733 6.72525V6.72525C3.30733 4.66059 5.53933 3.33325 8 3.33325C10.4607 3.33325 12.6927 4.66059 13.7027 6.72525V6.72525C13.8987 7.12592 14 7.56059 14 7.99992C14 8.43925 13.8987 8.87392 13.7027 9.27458V9.27458C12.6927 11.3393 10.4607 12.6666 8 12.6666C5.53933 12.6666 3.30733 11.3393 2.29733 9.27458V9.27458C2.10133 8.87392 2 8.43925 2 7.99992Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )


  const getBadgeClass = (status) => {
    const statusMap = {
      Pending: 'sh_dashbg-yellow sh_dashtext-yellow',
      'Pending Results':'sh_dashbg-yellow sh_dashtext-yellow',
      'Ready to review': 'sh_dashbg-blue sh_dashtext-blue',
      Complete: 'sh_dashbg-green sh_dashtext-green',
      Passed: 'sh_dashbg-green sh_dashtext-green',
      Failed: 'sh_dashbg-red sh_dashtext-red',
      'NOT STARTED': 'sh_dashbg-grey sh_dashtext-grey cap',
      Incompleted: 'sh_dashbg-yellow sh_dashtext-yellow',
      Closed: 'sh_dashbg-grey sh_dashtext-grey',
    }
    return statusMap[status] || 'sh_dashbg-grey sh_dashtext-grey'
  }
  const ViewIcon = () => (
    <svg width="18" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.7683 8.59714C12.7442 9.57297 12.7442 11.1571 11.7683 12.1346C10.7925 13.1105 9.20834 13.1105 8.23084 12.1346C7.255 11.1588 7.255 9.57464 8.23084 8.59714C9.20834 7.61964 10.7917 7.61964 11.7683 8.59714"
        stroke="#1D2433"
        strokeOpacity="0.8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.5 10.3639C2.5 9.81468 2.62667 9.27135 2.87167 8.77052V8.77052C4.13417 6.18968 6.92417 4.53052 10 4.53052C13.0758 4.53052 15.8658 6.18968 17.1283 8.77052V8.77052C17.3733 9.27135 17.5 9.81468 17.5 10.3639C17.5 10.913 17.3733 11.4564 17.1283 11.9572V11.9572C15.8658 14.538 13.0758 16.1972 10 16.1972C6.92417 16.1972 4.13417 14.538 2.87167 11.9572V11.9572C2.62667 11.4564 2.5 10.913 2.5 10.3639Z"
        stroke="#1D2433"
        strokeOpacity="0.8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )


  return (
    <>
      <div 
        ref={containerRef}
        className={`${isFullscreen ? 'tblviewcard flip fullscreen-card' : 'col-lg-6 mb-2 pb-1'}`} 
        style={isFullscreen ? { 
          position: 'fixed', 
          top: initialPosition ? `${initialPosition.top}px` : '0', 
          left: initialPosition ? `${initialPosition.left}px` : '0', 
          transform: initialPosition ? 'none' : 'none',
          width: initialPosition ? `${initialPosition.width}px` : '100vw', 
          height: initialPosition ? `${initialPosition.height}px` : '100vh', 
          zIndex: 1040, 
          backgroundColor: '#fff', 
          padding: '20px', 
          borderRadius: initialPosition ? '8px' : '0', 
          boxShadow: initialPosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none', 
          transition: 'all 0.3s ease-in-out'
        } : { transition: 'all 0.3s ease-in-out' }}
      >
      <div className="card mb-0 border-0 shadow-none" style={isFullscreen ? { height: '100%', margin: 0 } : {}}>
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="text-md font-weight-bold mb-0">Incomplete Charting</h6>
            <div className="d-flex align-items-center">
              <a 
                className="btn sh_btniconnew" 
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (!isFullscreen && containerRef.current) {
                    const rect = containerRef.current.getBoundingClientRect()
                    const pos = {
                      top: rect.top,
                      left: rect.left,
                      width: rect.width,
                      height: rect.height
                    }
                    originalPositionRef.current = pos
                    setInitialPosition(pos)
                    setIsFullscreen(true)
                    // Trigger transition to center after initial position is rendered
                    setTimeout(() => {
                      setInitialPosition(null)
                    }, 50)
                  } else {
                    // When closing, animate from center back to original position
                    if (originalPositionRef.current) {
                      setInitialPosition(originalPositionRef.current)
                      // Wait for animation to complete, then close
                      setTimeout(() => {
                        setIsFullscreen(false)
                        setInitialPosition(null)
                        originalPositionRef.current = null
                        setSearchTerm('') // Clear search when exiting fullscreen
                      }, 300) // Match transition duration
                    } else {
                      setIsFullscreen(false)
                      setInitialPosition(null)
                      setSearchTerm('') // Clear search when exiting fullscreen
                    }
                  }
                }}
                title={isFullscreen ? 'Minimize' : 'Maximize'}
              >
                {isFullscreen ? <CloseIcon /> : <SettingsIcon />}
              </a>
            </div>
          </div>
        </div>
        <div className={`card-body ${isFullscreen ? '' : 'sh_dashtableoverflowcard'} px-0 pt-0 mt-2 pb-2`} style={isFullscreen ? { height: 'calc(100% - 60px)', overflow: 'auto', display: 'flex', flexDirection: 'column' } : {}}>
          {isFullscreen && (
            <div className="px-3 pt-2 pb-2 d-flex align-items-center justify-content-end" style={{ borderBottom: '1px solid #e0e0e0' }}>
              <div className="d-flex align-items-center">
                <div className="mr-2">
                  <input
                    type="text"
                    placeholder="Patient"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const trimmed = searchTerm.trim()
                        setSearchTerm(trimmed)
                        fetchIncompleteCharting(trimmed)
                      }
                    }}
                    className="form-control"
                    style={{ minWidth: '200px' }}
                  />
                </div>
                <div className="addAction">
                  <div className="btn-group">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        const trimmed = searchTerm.trim()
                        setSearchTerm(trimmed)
                        fetchIncompleteCharting(trimmed)
                      }}
                    >
                      Search
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setSearchTerm('')
                        setPagination(prev => ({ ...prev, page: 1 }))
                        // Trigger fetch with empty search term
                        fetchIncompleteCharting('')
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div style={isFullscreen ? { flex: 1, overflow: 'auto' } : {}}>
          <table className="table dataTable sh_tablenew nowrap table-hover" width="100%" cellSpacing="0" style={{ width: '100%' }}>
            <thead>
              <tr>
                <SortableTh label="PROVIDER" sortKey="provider" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="PATIENT" sortKey="patient" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="VISIT DATE" sortKey="visit_date" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="TYPE" sortKey="type" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="DAYS OLD" sortKey="days_old" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="STATUS" sortKey="status" currentSortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableTh label="ACTION" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-3">
                    <span>Loading...</span>
                  </td>
                </tr>
              ) : tableData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-3">
                    <span>No incomplete visits Exists</span>
                  </td>
                </tr>
              ) : (
                tableData.map((row, index) => (
                  <tr key={row.visit_id || index}>
                    <td className="forTable_textEllipsis">{row.provider}</td>
                    <td className="forTable_textEllipsis">{row.patient}</td>
                    <td className="forTable_textEllipsis">{row.visit_date}</td>
                    <td className="forTable_textEllipsis">{row.type}</td>
                    <td className="forTable_textEllipsis">{row.days_old}</td>
                    <td className="forTable_textEllipsis">
                      <span className={`badge ${getBadgeClass(row.status)}`}>{row.status}</span>
                    </td>
                    <td className="forTable_textEllipsis">
                      <a 
                        className="btn btn-icon px-1 btn-transparent-dark" 
                        title="View Visit" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (navigate && row.visit_id) {
                            const categoryForRedirect = row.screen_type || row.visit_category
                            const href = isWorkCompVisitCategory(categoryForRedirect)
                              ? `/visits/emr-details/${row.visit_id}`
                              : `/visits/visit-details/${row.visit_id}`
                            navigate(href)
                          }
                        }}
                      >
                        <ViewIcon />
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
          {isFullscreen && allData.length > 0 && pagination.totalRecords > pagination.perPage && (
            <div className="px-3 pb-2">
              <Pagination
                pagination={pagination}
                fetchTableRecords={handlePaginationChange}
                tableRows={{ data: tableData }}
              />
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  )
}

export default IncompleteCharting




